/**
 * Small drawn motifs that animate over the promotional hero slides.
 *
 * These replaced a decorative video loop. A looping MP4 cost a network request,
 * could not take the slide's own colour, and read as a lens flare rather than as
 * anything about the CHI Alliance. Drawn as inline SVG instead, each motif:
 *
 *   - costs nothing to fetch and cannot 404;
 *   - is one idea per slide, animated on the property it is about — connection
 *     draws links, community ripples outward, a calendar fills, a voice
 *     radiates, a lattice lights up;
 *   - stops entirely for reduced-motion visitors rather than merely slowing,
 *     since the motion is the whole content and a static frame reads as intended.
 *
 * Everything is drawn in white and a white-led gradient rather than a per-slide
 * tint: the motifs sit inside a frosted circle over photography, and white is the
 * only treatment that stays legible across every frame. `currentColor` still
 * carries the white so a caller can override it in one place.
 */
import { useEffect, useRef } from "react";
import gsap from "gsap";

export type MotifVariant = "connection" | "ripple" | "converge" | "waveform" | "lattice";

/** Node positions for the connection motif, in the 120x120 viewBox. */
const CONNECTION_NODES = [
  { cx: 22, cy: 37 }, { cx: 60, cy: 21 }, { cx: 98, cy: 43 },
  { cx: 34, cy: 85 }, { cx: 74, cy: 99 }, { cx: 60, cy: 61 },
] as const;

const CONNECTION_LINKS = [
  [5, 0], [5, 1], [5, 2], [5, 3], [5, 4], [0, 1], [3, 4],
] as const;

/**
 * Spoke endpoints for the converge motif: a pentagon around the centre.
 *
 * Shifted down 4.6 units because the pentagon's drawn extent centres on y=55.4,
 * not 60 — five points around a circle are not vertically symmetric.
 */
const CONVERGE_SPOKES = [
  { x: 60.0, y: 16.6 }, { x: 105.7, y: 49.8 }, { x: 88.2, y: 103.4 },
  { x: 31.8, y: 103.4 }, { x: 14.3, y: 49.8 },
] as const;

/** Bars for the waveform motif: nine columns centred on the axis. */
const WAVEFORM_BARS = [21, 30, 39, 48, 57, 66, 75, 84, 93] as const;

export default function PromoHeroMotif({
  variant,
  active,
}: {
  variant: MotifVariant;
  /** Only the visible slide animates; the rest hold still and cost nothing. */
  active: boolean;
}) {
  const rootRef = useRef<SVGSVGElement>(null);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    timelineRef.current?.kill();
    timelineRef.current = null;

    if (!active) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const q = gsap.utils.selector(root);
    const tl = gsap.timeline({ repeat: -1, defaults: { ease: "sine.inOut" } });
    timelineRef.current = tl;

    /*
     * The whole motif turns in depth while its own sequence plays inside.
     *
     * Applied to the svg element rather than to the drawn children: CSS 3D
     * transforms are reliable on an HTML-tree element and awkward on SVG
     * children, and rotating the plate as one object is what reads as a card
     * turning to face you rather than five shapes each skewing separately.
     */
    gsap.set(root, { transformPerspective: 620, transformOrigin: "50% 50%" });
    tl.fromTo(root,
      { rotationY: -42, rotationX: 14, scale: 0.82, opacity: 0.55 },
      { rotationY: 0, rotationX: 0, scale: 1, opacity: 1, duration: 1.25, ease: "power3.out" },
      0);

    if (variant === "connection") {
      // Links draw themselves outward from the centre node, then rest.
      tl.fromTo(q(".motif-link"),
        { strokeDasharray: 160, strokeDashoffset: 160, opacity: 0 },
        { strokeDashoffset: 0, opacity: 1, duration: 0.9, stagger: 0.11 })
        .fromTo(q(".motif-node"), { scale: 0.4, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.5, stagger: 0.07, transformOrigin: "center" }, 0.3)
        .to(q(".motif-node"), { scale: 1.18, duration: 0.7, stagger: { each: 0.09, yoyo: true, repeat: 1 }, transformOrigin: "center" }, "+=0.4")
        .to([q(".motif-link"), q(".motif-node")], { opacity: 0.25, duration: 1.1 }, "+=0.9");
    }

    if (variant === "ripple") {
      // Three rings leaving the centre on a stagger, so one is always in flight.
      tl.fromTo(q(".motif-ring"),
        { scale: 0.18, opacity: 0.85, transformOrigin: "center" },
        { scale: 1.25, opacity: 0, duration: 3.2, stagger: 1.05, ease: "sine.out" });
    }

    if (variant === "converge") {
      /*
       * Five paths travelling inward to a single point, then the point blooming.
       *
       * This replaced a literal calendar drawing. A calendar animates badly --
       * there is nothing in a grid of dates that wants to move -- and it said
       * "dates" when the slide is about people gathering. Converging spokes say
       * the same thing about an event without illustrating a wall planner.
       */
      const spokes = q(".motif-spoke");
      tl.set(spokes, { strokeDasharray: 52, strokeDashoffset: 52 })
        .set(q(".motif-burst"), { scale: 0.2, opacity: 0, transformOrigin: "center" })
        .set(q(".motif-core"), { scale: 0, opacity: 0, transformOrigin: "center" })
        .to(spokes, { strokeDashoffset: 0, duration: 0.85, stagger: 0.09, ease: "power2.inOut" })
        .to(q(".motif-core"), { scale: 1, opacity: 1, duration: 0.42, ease: "power3.out" }, "-=0.22")
        // The ring leaves the point the spokes just reached, which is what makes
        // the arrival read as an arrival rather than a shape appearing.
        .to(q(".motif-burst"), { scale: 1.5, opacity: 0, duration: 1.5, ease: "power2.out" }, "-=0.28")
        .to(q(".motif-core"), { scale: 1.14, duration: 1, yoyo: true, repeat: 1, ease: "sine.inOut" }, "-=1.15")
        .to([spokes, q(".motif-core")], { opacity: 0.24, duration: 0.9, ease: "power1.inOut" }, "+=0.5");
    }

    if (variant === "waveform") {
      /*
       * A voice, drawn as level rather than as rings.
       *
       * Concentric arcs could only fade in and out; there was no property in them
       * that carried speech. Bars have amplitude, so the motion is the subject:
       * each phase below is a different shape of the same voice, eased so the
       * wave travels across the bars instead of flashing.
       */
      const bars = q(".motif-bar");
      const phase = (offset: number) => (index: number) =>
        0.24 + 0.76 * Math.abs(Math.sin(index * 0.78 + offset));

      tl.fromTo(bars,
        { scaleY: 0.1, opacity: 0.4, transformOrigin: "center" },
        { scaleY: phase(0), opacity: 1, duration: 0.55, stagger: 0.055, ease: "power2.out" })
        .to(bars, { scaleY: phase(1.9), duration: 0.62, stagger: 0.045, ease: "sine.inOut", transformOrigin: "center" })
        .to(bars, { scaleY: phase(3.6), duration: 0.62, stagger: 0.045, ease: "sine.inOut", transformOrigin: "center" })
        .to(bars, { scaleY: phase(5.2), duration: 0.62, stagger: 0.045, ease: "sine.inOut", transformOrigin: "center" })
        .to(bars, { scaleY: 0.16, opacity: 0.3, duration: 0.8, stagger: 0.035, ease: "power1.inOut", transformOrigin: "center" }, "+=0.35");
    }

    if (variant === "lattice") {
      // Points light in a wandering order so the lattice never reads as a grid sweep.
      tl.fromTo(q(".motif-point"), { opacity: 0.16, scale: 0.7, transformOrigin: "center" },
        { opacity: 1, scale: 1.1, duration: 0.5, stagger: { each: 0.09, from: "random" } })
        .to(q(".motif-point"), { opacity: 0.2, scale: 0.85, duration: 0.8, stagger: { each: 0.07, from: "random" } }, "+=0.3");
    }

    tl.to(root, { rotationY: 30, rotationX: -8, scale: 0.9, duration: 1.5, ease: "sine.inOut" });

    return () => {
      timelineRef.current?.kill();
      timelineRef.current = null;
    };
  }, [active, variant]);

  return (
    <svg
      ref={rootRef}
      className="promo-hero__motif"
      viewBox="0 0 120 120"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {/* White-led so the motif reads on any photograph, with the brand pair
            carried in the tail of the ramp rather than across the whole stroke. */}
        <linearGradient id={`motif-ramp-${variant}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.98" />
          <stop offset="52%" stopColor="#FFFFFF" stopOpacity="0.86" />
          <stop offset="100%" stopColor="#F9A8D4" stopOpacity="0.82" />
        </linearGradient>
      </defs>
      {variant === "connection" && (
        <g fill="none" stroke={`url(#motif-ramp-${variant})`} strokeWidth="1.1" strokeLinecap="round">
          {CONNECTION_LINKS.map(([from, to]) => (
            <line
              className="motif-link"
              key={`${from}-${to}`}
              x1={CONNECTION_NODES[from].cx} y1={CONNECTION_NODES[from].cy}
              x2={CONNECTION_NODES[to].cx} y2={CONNECTION_NODES[to].cy}
              opacity="0.5"
            />
          ))}
          {CONNECTION_NODES.map((node, index) => (
            <circle
              className="motif-node"
              key={index}
              cx={node.cx} cy={node.cy}
              r={index === 5 ? 5.4 : 3.6}
              fill={`url(#motif-ramp-${variant})`}
              stroke="none"
            />
          ))}
        </g>
      )}

      {variant === "ripple" && (
        <g fill="none" stroke={`url(#motif-ramp-${variant})`} strokeWidth="1.4">
          {[0, 1, 2].map(index => (
            <circle className="motif-ring" key={index} cx="60" cy="60" r="46" />
          ))}
          <circle cx="60" cy="60" r="5" fill={`url(#motif-ramp-${variant})`} stroke="none" opacity="0.9" />
        </g>
      )}

      {variant === "converge" && (
        <g fill="none" stroke={`url(#motif-ramp-${variant})`} strokeWidth="1.5" strokeLinecap="round">
          {CONVERGE_SPOKES.map(spoke => (
            <line
              className="motif-spoke"
              key={`${spoke.x}-${spoke.y}`}
              x1={spoke.x} y1={spoke.y} x2="60" y2="60"
              opacity="0.85"
            />
          ))}
          <circle className="motif-burst" cx="60" cy="60" r="26" strokeWidth="1.6" />
          <circle className="motif-core" cx="60" cy="60" r="7.2" fill={`url(#motif-ramp-${variant})`} stroke="none" />
        </g>
      )}

      {variant === "waveform" && (
        <g fill={`url(#motif-ramp-${variant})`} stroke="none">
          {WAVEFORM_BARS.map(x => (
            <rect
              className="motif-bar"
              key={x}
              x={x} y="34" width="6" height="52" rx="3"
            />
          ))}
        </g>
      )}

      {variant === "lattice" && (
        <g stroke={`url(#motif-ramp-${variant})`} strokeWidth="0.7" fill="none">
          {[0, 1, 2, 3].map(row =>
            [0, 1, 2, 3].map(col => (
              <circle
                className="motif-point"
                key={`${row}-${col}`}
                cx={24 + col * 24}
                cy={24 + row * 24}
                r="3.4"
                fill={`url(#motif-ramp-${variant})`}
                stroke="none"
                opacity="0.35"
              />
            )),
          )}
          <path d="M24 24 L96 24 M24 48 L96 48 M24 72 L96 72 M24 96 L96 96" opacity="0.14" />
          <path d="M24 24 L24 96 M48 24 L48 96 M72 24 L72 96 M96 24 L96 96" opacity="0.14" />
        </g>
      )}
    </svg>
  );
}
